import type { ItemProps } from "@bedrock/domain";
import { Icon, IconButton } from "@bedrock/ui";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from "pdfjs-dist";

type PdfProps = Extract<ItemProps, { kind: "pdf" }>;
type PdfViewerVariant = "canvas" | "detail";
type ReaderStatus = "idle" | "loading" | "ready" | "error";

const PDF_ZOOM_MIN = 0.75;
const PDF_ZOOM_MAX = 2;
const PDF_ZOOM_STEP = 0.25;
let pdfRenderQueue: Promise<void> = Promise.resolve();

export function clampPdfZoom(zoom: number) {
  return Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, zoom));
}

function isRenderCancellation(error: unknown) {
  return error instanceof Error && error.name === "RenderingCancelledException";
}

function schedulePdfRender(render: () => Promise<void>) {
  const scheduled = pdfRenderQueue.then(render, render);
  pdfRenderQueue = scheduled.catch(() => undefined);
  return scheduled;
}

function PdfReaderState({ status, hasSource }: { status: ReaderStatus; hasSource: boolean }) {
  if (status === "ready") return null;
  if (!hasSource || status === "error") {
    return <div className="pdf-reader__state" role={status === "error" ? "alert" : undefined}>
      <Icon name="pdf"/>
      <strong>Preview unavailable</strong>
      <span>{hasSource ? "Download this PDF to open it externally." : "This PDF is not available offline yet."}</span>
    </div>;
  }
  return <div className="pdf-reader__loading" aria-label="Loading PDF preview">
    <span/><span/><span/>
  </div>;
}

function PdfPageCanvas({ document, pageNumber, pageCount, stageSize, variant, zoom, onError, onRendered }: {
  document: PDFDocumentProxy;
  pageNumber: number;
  pageCount: number;
  stageSize: { width: number; height: number };
  variant: PdfViewerVariant;
  zoom: number;
  onError: () => void;
  onRendered?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || stageSize.width <= 0 || stageSize.height <= 0) return;
    let renderTask: RenderTask | null = null;
    let disposed = false;
    const render = async () => {
      try {
        const page = await document.getPage(pageNumber);
        if (disposed) {
          page.cleanup();
          return;
        }
        const baseViewport = page.getViewport({ scale: 1 });
        const stageInset = variant === "detail" ? 48 : 16;
        const availableWidth = Math.max(1, stageSize.width - stageInset);
        const availableHeight = Math.max(1, stageSize.height - stageInset);
        const fitScale = variant === "detail"
          ? availableWidth / baseViewport.width
          : Math.min(availableWidth / baseViewport.width, availableHeight / baseViewport.height);
        const viewport = page.getViewport({ scale: fitScale * (variant === "canvas" ? 1 : zoom) });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
        canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.dataset["ready"] = "false";
        setReady(false);
        renderTask = page.render({
          canvas,
          viewport,
          intent: window.document.visibilityState === "hidden" ? "print" : "display",
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0]
        });
        await renderTask.promise;
        if (!disposed) {
          canvas.dataset["ready"] = "true";
          setReady(true);
          onRendered?.();
        }
        page.cleanup();
      } catch (error) {
        if (!disposed && !isRenderCancellation(error)) onError();
      }
    };
    void schedulePdfRender(render);
    return () => {
      disposed = true;
      renderTask?.cancel();
    };
  }, [document, onError, onRendered, pageNumber, stageSize.height, stageSize.width, variant, zoom]);

  return <figure className={`pdf-reader__page-frame${ready ? " is-ready" : ""}`} data-page-number={pageNumber}>
    <canvas ref={canvasRef} className="pdf-reader__page" role="img" aria-label={`Page ${pageNumber} of ${pageCount}`}/>
  </figure>;
}

export function PdfViewer({ props, variant }: { props: PdfProps; variant: PdfViewerVariant }) {
  const source = props.localUrl;
  const stageRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [status, setStatus] = useState<ReaderStatus>(source ? "loading" : "idle");
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const handleRenderError = useCallback(() => setStatus("error"), []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => setStageSize({ width: stage.clientWidth, height: stage.clientHeight });
    measure();
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(stage);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    let loadingTask: PDFDocumentLoadingTask | null = null;
    let disposed = false;
    setDocument(null);
    setPageNumber(1);
    setZoom(1);
    setStatus(source ? "loading" : "idle");
    if (!source) return;

    void import("pdfjs-dist").then(async (pdfjs) => {
      if (disposed) return;
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      loadingTask = pdfjs.getDocument({ url: source });
      try {
        const loaded = await loadingTask.promise;
        if (disposed) {
          await loadingTask.destroy();
          return;
        }
        setDocument(loaded);
        setStatus("ready");
      } catch {
        if (!disposed) setStatus("error");
      }
    }).catch(() => {
      if (!disposed) setStatus("error");
    });

    return () => {
      disposed = true;
      void loadingTask?.destroy();
    };
  }, [source]);

  const pageCount = document?.numPages ?? 0;
  const pageNumbers = document
    ? Array.from({ length: variant === "canvas" ? 1 : document.numPages }, (_, index) => index + 1)
    : [];

  const updateCurrentPage = useCallback(() => {
    const stage = stageRef.current;
    const pages = pagesRef.current;
    if (variant !== "detail" || !stage || !pages) return;
    const marker = stage.getBoundingClientRect().top + stage.clientHeight * 0.38;
    let closestPage = 1;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const element of pages.querySelectorAll<HTMLElement>("[data-page-number]")) {
      const bounds = element.getBoundingClientRect();
      const distance = marker < bounds.top
        ? bounds.top - marker
        : marker > bounds.bottom
          ? marker - bounds.bottom
          : 0;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = Number(element.dataset["pageNumber"] ?? 1);
      }
    }
    setPageNumber(closestPage);
  }, [variant]);

  useEffect(() => {
    const stage = stageRef.current;
    const pages = pagesRef.current;
    if (variant !== "detail" || !stage || !pages || !document) return;
    updateCurrentPage();
    stage.addEventListener("scroll", updateCurrentPage, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateCurrentPage);
    observer?.observe(pages);
    return () => {
      stage.removeEventListener("scroll", updateCurrentPage);
      observer?.disconnect();
    };
  }, [document, updateCurrentPage, variant]);

  const changeZoom = (direction: -1 | 1) => setZoom((current) => clampPdfZoom(current + direction * PDF_ZOOM_STEP));
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (variant !== "detail" || event.target instanceof HTMLButtonElement) return;
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;
      stage.scrollBy({
        top: stage.clientHeight * (event.key === "PageUp" ? -0.84 : 0.84),
        behavior: "auto"
      });
    }
  };

  return <section className={`pdf-reader pdf-reader--${variant}`} aria-label={`${props.filename} PDF reader`} tabIndex={variant === "detail" ? 0 : -1} onKeyDown={onKeyDown}>
    {variant === "detail" && <header className="pdf-reader__toolbar">
      <div className="pdf-reader__identity" title={props.filename}>
        <span><Icon name="pdf"/></span>
        <strong>{props.filename}</strong>
      </div>
      <output className="pdf-reader__page-status" aria-live="polite" aria-label={`Page ${pageNumber} of ${pageCount || 1}`}><strong>{pageNumber}</strong><span>/</span>{pageCount || 1}</output>
      <div className="pdf-reader__zoom-controls" aria-label="PDF zoom controls">
        <IconButton label="Zoom out" tooltipPlacement="bottom" icon="zoom-out" disabled={zoom <= PDF_ZOOM_MIN || status !== "ready"} onClick={() => changeZoom(-1)}/>
        <button className="pdf-reader__fit" type="button" aria-label="Fit width" disabled={status !== "ready"} onClick={() => setZoom(1)}><Icon name="fit-view"/><span>{Math.round(zoom * 100)}%</span></button>
        <IconButton label="Zoom in" tooltipPlacement="bottom" icon="zoom-in" disabled={zoom >= PDF_ZOOM_MAX || status !== "ready"} onClick={() => changeZoom(1)}/>
      </div>
    </header>}
    <div className="pdf-reader__stage" ref={stageRef}>
      <PdfReaderState status={status} hasSource={Boolean(source)}/>
      <div className="pdf-reader__pages" ref={pagesRef} aria-label={variant === "detail" ? "PDF pages" : undefined}>
        {status === "ready" && document && pageNumbers.map((number) => <PdfPageCanvas
          key={number}
          document={document}
          pageNumber={number}
          pageCount={pageCount}
          stageSize={stageSize}
          variant={variant}
          zoom={zoom}
          onError={handleRenderError}
          {...(variant === "detail" ? { onRendered: updateCurrentPage } : {})}
        />)}
      </div>
    </div>
  </section>;
}
